import Vue from 'vue'
import VueRouter from 'vue-router'

// Routes
// import { canNavigate } from '@/libs/acl/routeProtection'
import { isUserLoggedIn, getUserData } from '@/auth/utils'
import { createPermissionGuard } from '@/utils/routeGuard'
import supportAgentAccess from '@/utils/support-agent-access.cjs'
import store from '@/store'
import { get_current_user_permissions } from '@/apis/auth'
import { hasAuthSessionChanged } from '@/auth/session'

import adminAuth from './routes/admin-app/auth'
import adminDashboard from './routes/admin-app/dashboard'
import clientAuth from './routes/client-app/auth'
import clientDashboard from './routes/client-app/dashboard'

import pages from './routes/pages'

const { supportHomeRoute } = supportAgentAccess

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    redirect: () => (
      isUserLoggedIn()
        ? { name: supportHomeRoute(getUserData()) }
        : { name: 'auth-login' }
    ),
  },
     ...pages,
     ...adminAuth,
     ...adminDashboard,
     ...clientAuth,
     ...clientDashboard,
  {
    path: '*',
    redirect: 'error-404',
  },
]

const PREFETCH_ROUTE_NAMES = [
  'dashboard',
  'documents',
  'proposals',
  'invoices',
  'payments',
  'archive',
  'settings',
  'support-tickets',
]

let topLevelRouteChunksPrefetched = false

function runWhenIdle(callback) {
  if (typeof window === 'undefined') return
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 4000 })
    return
  }
  setTimeout(callback, 1500)
}

function prefetchTopLevelRouteChunks(routerInstance) {
  if (topLevelRouteChunksPrefetched) return
  topLevelRouteChunksPrefetched = true

  runWhenIdle(() => {
    const routeRecords = routerInstance.options.routes || []
    routeRecords
      .filter(route => PREFETCH_ROUTE_NAMES.includes(route.name))
      .forEach(route => {
        if (typeof route.component === 'function') {
          route.component().catch(() => {})
        }
      })
  })
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const getMagicLinkKey = route => (
    route.params?.key
    || route.query?.key
    || route.query?.magicKey
    || route.query?.magic_link
    || route.query?.shareKey
    || route.query?.token
    || ""
  )

  const redirectMagicLinkToSharedPreview = () => {
    const key = getMagicLinkKey(to)
    if (!key) return false

    next({
      name: "shared-documents",
      params: { key },
      query: {
        route: to.query?.route,
      },
    })
    return true
  }

  const hasRequiredAuth = requiredRoles => {
    const userData = getUserData()
    return requiredRoles.includes(userData.role)
  }

  const isLoggedIn = isUserLoggedIn()

  // First check basic auth
  if (to.meta.requiresAuth && !isLoggedIn) {
    if (["document-details", "recieved-documents"].includes(to.name) && redirectMagicLinkToSharedPreview()) {
      return
    }
    next({ name: 'auth-login', query: hasAuthSessionChanged() ? { sessionChanged: '1' } : {} })
    return
  } if ((to.meta.requiresAuth && isLoggedIn) && !hasRequiredAuth(to.meta.roles)) {
    next({ name: 'misc-not-authorized' })
    return
  }

  // Initialize permissions on first protected route access if not already loaded
  if (isLoggedIn && to.meta.requiresAuth && !store.state.permissions?.loaded) {
    console.log('📋 Initializing user permissions on page reload/startup...')
    try {
      const response = await get_current_user_permissions()
      if (response && response.data) {
        const permissionsData = response.data
        await store.dispatch('permissions/updatePermissionsFromAPI', permissionsData)
        console.log('✅ Permissions loaded successfully:', permissionsData)
      } else {
        throw new Error('Unable to load account permissions')
      }
    } catch (error) {
      console.error('❌ Failed to load permissions on page reload:', error)
      store.commit('permissions/RESET_PERMISSIONS')
      next({ name: 'auth-login', query: { permissionsError: '1' } })
      return
    }
  }

  // Then apply permission guard (checks permission-based access)
  // Only apply if permissions are loaded to avoid redirect loops
  if (isLoggedIn && store.state.permissions?.loaded) {
    const permissionGuard = createPermissionGuard(store)
    permissionGuard(to, from, next)
  } else {
    next()
  }
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
  if (router.currentRoute?.meta?.requiresAuth) {
    prefetchTopLevelRouteChunks(router)
  }
})

export default router
